import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChildren,
  DestroyRef,
  effect,
  inject,
  input,
  QueryList,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RippleModule } from 'primeng/ripple';
import { Stepper, StepperModule } from 'primeng/stepper';
import { IconComponent } from '../../icon/icon/icon.component';
import { StepComponent } from './components/step/step.component';
import { StepTemplateContextPipe } from './pipes/step-template-context.pipe';
import { StepAccessChecker } from './utils/step-access-checker';

/**
 * @description
 * Function component - Stepper.
 * Features of use:
 *
 * -  Works together with a component "osp-step" and two directives "ospStepContent" and "ospStepFooter".
 *    This is a reason to import the StepperModule witch includes these entities.
 *
 * -  The component "osp-step" consumes two <ng-template> as a projected content
 *    for a step-content and a step-footer (with corresponding directives "ospStepContent" and "ospStepFooter").
 *
 * -  The directive "ospStepContent" has template variables:
 *      - "index"
 *      - "active"
 *      - "highlighted"
 *      - isValid
 *      - "prevCallback"
 *      - "nextCallback"
 *
 * -  The directive "ospStepFooter" has template variables:
 *      - "index"
 *      - isValid
 *      - "prevCallback"
 *      - "nextCallback"
 *
 * -  Example of using:
 *  <osp-stepper>
 *    <osp-step [title]="'Title'" [icon]="icon" [control]="control">
 *      <ng-template
 *        ospStepContent
 *        let-index="index"
 *        let-active="active"
 *        let-highlighted="highlighted"
 *        let-prevCallback="prevCallback"
 *        let-nextCallback="nextCallback"
 *      > ... </ng-template>
 *
 *      <ng-template
 *        ospStepFooter
 *        let-index="index"
 *        let-prevCallback="prevCallback"
 *        let-nextCallback="nextCallback"
 *      > ... </ng-template>
 *    </osp-step>
 *
 *    <osp-step> ... </osp-step>
 *  </osp-stepper>
 *
 */

@Component({
  selector: 'osp-stepper',
  standalone: true,
  imports: [StepperModule, NgTemplateOutlet, NgClass, IconComponent, RippleModule, StepTemplateContextPipe],
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class StepperComponent implements AfterContentInit {
  private destroyRef = inject(DestroyRef);

  @ViewChild(Stepper) stepper!: Stepper;
  @ContentChildren(StepComponent) stepComponents!: QueryList<StepComponent>;

  highlightVisitHistory = input(false);

  protected activeIndex = signal<number>(0);
  protected steps = signal<StepComponent[] | null>(null);
  protected stepAmount = computed(() => this.steps()?.length ?? 0);
  protected maxStepIndexVisited = computed(() =>
    this.highlightVisitHistory() ? this.stepChecker.maxStepIndexVisited() : this.activeIndex(),
  );
  private stepChecker = new StepAccessChecker();

  constructor() {
    effect(() => {
      if (this.activeIndex() > -1) {
        setTimeout(() => {
          document.querySelector('.p-stepper-panels')?.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
        }, 0);
      }
    });
  }

  ngAfterContentInit(): void {
    this.stepComponentsChanges(this.stepComponents);
    this.stepComponents.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.stepComponentsChanges(this.stepComponents, this.activeIndex()));
  }

  updateActiveStep(index: number): void {
    this.stepChecker.updateActiveStep(this.stepper, index, this.activeIndex());
  }

  trackStep(step: StepComponent): string {
    return step.title + ' ' + step.icon;
  }

  private stepComponentsChanges(stepComponents: QueryList<StepComponent>, activeIndex: number = 0): void {
    this.steps.set(stepComponents.toArray());
    this.stepChecker.setSteps(this.steps(), activeIndex);
  }
}
